import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';
import GuestAddOnTooltip from '@/features/booking/components/GuestAddOnTooltip';
import type { AddOnScheduleData } from '@/features/booking/types/ticketBookingTypes';
import type { FloorPlanResponse, FloorPlanItemResponse } from '@/features/booking/types/floorPlanTypes';

import MobileTablePreviewCard from './MobileTablePreviewCard';
import MobileAddOnSelector from './MobileAddOnSelector';
import { MOBILE_POPUP_HEIGHT } from '@/features/booking/constants/mobileBooking';
import SwipeModal from '@/components/ui/SwipeModal';

interface MobileTableSelectionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  // Preview Card Props
  floorPlan: FloorPlanResponse | null;
  selectedTable: string | null;
  floorPlanItems: FloorPlanItemResponse[];
  pricePerGuest: number;
  currency?: string;
  categoryImageId?: string | null;
  selectedTimeSlot?: string | null;
  businessTimeZone?: string;
  tableSize?: number | null;
  itemType?: string;
  onAddClick: () => void;
  guestCount: number;
  
  // Addon Props
  addOns: AddOnScheduleData[];
  selectedAddOns: Record<string, number>;
  onAddOnToggle: (addOnId: string, isSelected: boolean, guestIndex?: number) => void;
  addOnIdsPerGuest: string[][];
  onQuantityChange: (addOnId: string, guestIndex: number, quantity: number) => void;
  
  // Extra info for addons early entry
  eventStartTime?: string | null;
  earlyEntryDuration?: number | null;
}

const MobileTableSelectionPopup: React.FC<MobileTableSelectionPopupProps> = ({
  isOpen,
  onClose,
  floorPlan,
  selectedTable,
  floorPlanItems,
  pricePerGuest,
  currency = 'USD',
  categoryImageId,
  selectedTimeSlot,
  businessTimeZone,
  tableSize,
  itemType,
  onAddClick,
  guestCount,
  addOns,
  selectedAddOns,
  onAddOnToggle,
  addOnIdsPerGuest,
  onQuantityChange,
  eventStartTime,
  earlyEntryDuration,
}) => {
  const [view, setView] = useState<'main' | 'addonDetail'>('main');
  const [activeAddOn, setActiveAddOn] = useState<AddOnScheduleData | null>(null);

  // Reset view when popup closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setView('main');
        setActiveAddOn(null);
      }, 300); // Wait for transition
    }
  }, [isOpen]);

  const handleAddOnClick = (addOnSchedule: AddOnScheduleData) => {
    setActiveAddOn(addOnSchedule);
    setView('addonDetail');
  };

  const handleBackToMain = () => {
    setView('main');
    setActiveAddOn(null);
  };

  return (
    <SwipeModal 
      isOpen={isOpen && !!selectedTable} 
      onClose={() => {
        if (view === 'addonDetail') {
          handleBackToMain();
        } else {
          onClose();
        }
      }}
      height={`calc(${MOBILE_POPUP_HEIGHT} + 80px)`}
      paddingBottom="calc(env(safe-area-inset-bottom, 0px) + 80px)"
      overlayHandle={true}
    >
          <h2 className="sr-only">Table Details and Selection</h2>
          <p className="sr-only">Review table details, add-ons, and proceed to booking.</p>
          
          <div className="flex-1 overflow-y-auto scrollbar-hide relative w-full h-full">
            <AnimatePresence mode='popLayout' initial={false}>
                {view === 'main' ? (
                  <motion.div
                    key="main-view"
                    initial={{ opacity: 0, x: -100, filter: 'blur(4px)' }}
                    animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, x: -100, filter: 'blur(4px)' }}
                    transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                    className="flex flex-col pb-24" // Extra padding to ensure content above floating summary is reachable
                  >
                    <div className="w-full">
                       <MobileTablePreviewCard
                        floorPlan={floorPlan}
                        selectedTable={selectedTable}
                        floorPlanItems={floorPlanItems}
                        pricePerGuest={pricePerGuest}
                        currency={currency}
                        categoryImageId={categoryImageId}
                        selectedTimeSlot={selectedTimeSlot}
                        businessTimeZone={businessTimeZone}
                        tableSize={tableSize}
                        itemType={itemType}
                        onAddClick={onAddClick}
                        guestCount={guestCount}
                      />
                    </div>
                    
                    <div className="mt-4 px-4">
                      <MobileAddOnSelector
                        addOns={addOns}
                        selectedAddOns={selectedAddOns}
                        onAddOnToggle={onAddOnToggle}
                        onAddOnClick={handleAddOnClick}
                        guestCount={guestCount}
                        addOnIdsPerGuest={addOnIdsPerGuest}
                        currency={currency}
                      />
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="addon-detail-view"
                    initial={{ opacity: 0, x: 100, filter: 'blur(4px)' }}
                    animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, x: 100, filter: 'blur(4px)' }}
                    transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                    className="w-full h-full bg-[#0a0a0a] flex flex-col"
                  >
                    {activeAddOn && (
                       <AddOnDetailInternal 
                         addOn={activeAddOn}
                         isSelected={selectedAddOns[activeAddOn.add_on.id] > 0}
                         onToggle={onAddOnToggle}
                         onQuantityChange={onQuantityChange}
                         guestCount={guestCount}
                         remainingInventory={activeAddOn.remaining_inventory}
                         addOnIdsPerGuest={addOnIdsPerGuest}
                         allAddOns={addOns}
                         eventStartTime={eventStartTime}
                         earlyEntryDuration={earlyEntryDuration}
                         timezone={businessTimeZone}
                         currency={currency}
                         onBack={handleBackToMain}
                       />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
    </SwipeModal>
  );
};

// Internal component for Addon Details to avoid re-writing 300 lines of complex UI inline
const AddOnDetailInternal: React.FC<{
  addOn: AddOnScheduleData;
  isSelected: boolean;
  onToggle: (addOnId: string, isSelected: boolean, guestIndex?: number) => void;
  onQuantityChange: (addOnId: string, guestIndex: number, quantity: number) => void;
  guestCount: number;
  remainingInventory: number | null;
  addOnIdsPerGuest: string[][];
  allAddOns: AddOnScheduleData[];
  eventStartTime?: string | null;
  earlyEntryDuration?: number | null;
  timezone?: string;
  currency: string;
  onBack: () => void;
}> = ({
  addOn, isSelected, onToggle, onQuantityChange, guestCount, remainingInventory, addOnIdsPerGuest, allAddOns, eventStartTime, earlyEntryDuration, timezone, currency, onBack
}) => {
  const formatPrice = (amount: number) => formatCurrencyForDisplay(amount, currency);
  const hasValidImage = addOn.add_on.image_id && addOn.add_on.image_id !== '00000000-0000-0000-0000-000000000000';
  const imageUrl = hasValidImage ? getSignedMediaUrl(addOn.add_on.image_id, 'image').url : null;
  
  const isUnlimited = remainingInventory === null;
  const canApplyToAllGuests = isUnlimited || (remainingInventory as number) >= guestCount;
  const isInventoryLimited = !isUnlimited && (remainingInventory as number) < guestCount && (remainingInventory as number) > 0;
  const addOnId = addOn.add_on.id;
  const allowMultiple = addOn.add_on.allow_multiple_per_guest;

  const handleToggle = () => {
    if (guestCount > 0) {
      onToggle(addOnId, !isSelected);
    }
  };

  const getAddOnCount = () => {
    return addOnIdsPerGuest.reduce((count, guestAddOns) => 
      count + guestAddOns.filter(id => id === addOnId).length, 0
    );
  };

  const getGuestQuantity = (guestIndex: number): number => {
    const guestAddOns = addOnIdsPerGuest[guestIndex] || [];
    return guestAddOns.filter(id => id === addOnId).length;
  };

  const getRemainingForGuest = (guestIndex: number): number | null => {
    if (isUnlimited) return null;
    let usedByOthers = 0;
    addOnIdsPerGuest.forEach((guestAddOns, idx) => {
      if (idx !== guestIndex) {
        usedByOthers += guestAddOns.filter(id => id === addOnId).length;
      }
    });
    return (remainingInventory as number) - usedByOthers;
  };

  const count = getAddOnCount();

  return (
    <div className="w-full flex-1 flex flex-col">
       {/* Details Header inside popup */}
       <div 
         className="sticky top-0 z-20 flex flex-col bg-[#0a0a0a] border-b border-gray-800 shrink-0 pt-6"
       >
          {/* Back button + title */}
          <div className="flex items-center px-4 pb-3 pt-1">
            <button 
              onClick={onBack}
              className="mr-3 p-1.5 bg-gray-800 rounded-full text-white active:scale-95 transition-transform"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="text-lg font-bold text-white flex-1 truncate">{addOn.add_on.name}</h3>
          </div>
       </div>

       <div 
         className="p-4 space-y-6"
       >
          {/* Image */}
          <div className="relative h-48 sm:h-64 rounded-xl overflow-hidden border border-white/5">
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: imageUrl 
                  ? `url(${imageUrl})` 
                  : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111] to-transparent" />
          </div>

          {/* Info */}
          <div className="space-y-3 pb-8">
             <p className="text-gray-300 leading-relaxed text-sm">{addOn.add_on.description}</p>
             <div className="flex items-center justify-between">
                <div className="text-2xl font-bold text-blue-400">
                  {formatPrice(addOn.price)}
                </div>
                {isUnlimited ? (
                  <div className="text-sm text-gray-400 font-semibold tracking-wide">Unlimited</div>
                ) : (remainingInventory as number) < 10 ? (
                  <div className="text-sm font-bold text-red-400">
                    Only {remainingInventory} left!
                  </div>
                ) : (
                  <div className="text-sm font-semibold text-gray-400">
                    {remainingInventory} available
                  </div>
                )}
             </div>
          </div>

          {/* Inventory Warning */}
          {isInventoryLimited && (
            <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <div className="text-yellow-400">⚠️</div>
                <div>
                  <h5 className="text-yellow-400 font-semibold text-sm mb-1">Limited Inventory</h5>
                  <p className="text-yellow-200/80 text-xs leading-relaxed">
                    Only {remainingInventory} available for {guestCount} guests. You will need to manually assign it below.
                  </p>
                </div>
              </div>
            </div>
          )}

           {/* Toggle */}
           <div className="flex items-center justify-between p-4 bg-gray-800/80 rounded-xl border border-white/5">
              <div className="flex-1 pr-4">
                <div className="text-white font-bold mb-0.5">
                  {isSelected ? 'Selected' : 'Add to Booking'}
                </div>
                <div className="text-xs text-gray-400 leading-tight">
                  {isSelected 
                    ? count > 0 
                      ? (
                          <span>
                            {count} qty × {formatPrice(addOn.price)} = <span className="text-blue-400 font-semibold">{formatPrice(addOn.price * count)}</span>
                          </span>
                        )
                      : 'Selected but not assigned'
                    : canApplyToAllGuests 
                      ? `Will apply to all ${guestCount} guests`
                      : 'Manual assignment required'
                  }
                </div>
              </div>
              <button
                onClick={handleToggle}
                disabled={guestCount === 0}
                className={`w-14 h-7 shrink-0 rounded-full transition-colors duration-200 ${
                  guestCount === 0
                    ? 'bg-gray-600 cursor-not-allowed opacity-50'
                    : isSelected 
                    ? 'bg-blue-600' 
                    : 'bg-white/30 hover:bg-white/40'
                }`}
              >
                <div
                  className={`w-6 h-6 bg-white rounded-full transition-transform duration-200 ${
                    isSelected ? 'translate-x-7' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

          {/* Early Entry */}
          {addOn.add_on.name === 'Early Entry' && addOn.add_on.is_platform_add_on && eventStartTime && earlyEntryDuration && (
              <div className="bg-gradient-to-r from-emerald-900/40 to-teal-900/40 border border-emerald-500/50 rounded-lg p-3 mt-4">
                <h5 className="text-emerald-300 font-bold text-sm mb-1">You can take entry early!</h5>
                <p className="text-emerald-100/90 text-xs">
                  Earliest Entry Time:{' '}
                  <span className="font-bold text-white">
                    {new Date(new Date(eventStartTime).getTime() - (earlyEntryDuration / 1_000_000)).toLocaleTimeString('en-US', { 
                      hour: '2-digit', minute: '2-digit', hour12: true, timeZone: timezone
                    })}
                  </span>
                </p>
              </div>
          )}

            {/* Guest Select */}
            <AnimatePresence>
              {isSelected && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="pt-2"
                >
                  <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-1">
                    {allowMultiple ? 'Quantity per Guest' : 'Assign to Guests'}
                  </h5>
                  <div className={`grid gap-1.5 sm:gap-2 ${allowMultiple ? 'grid-cols-2' : 'grid-cols-4'}`}>
                    {Array.from({ length: guestCount }, (_, guestIndex) => {
                      const guestAddOns = addOnIdsPerGuest[guestIndex] || [];
                      const hasAddOn = guestAddOns.includes(addOnId);
                      const quantity = getGuestQuantity(guestIndex);
                      const remaining = getRemainingForGuest(guestIndex);
                      const maxForGuest = remaining === null ? Infinity : remaining + quantity;

                      return (
                        <div key={guestIndex}>
                           {allowMultiple && onQuantityChange ? (
                              <div className={`flex flex-col items-center justify-center p-3 sm:p-4 gap-2.5 rounded-xl border transition-colors ${
                                quantity > 0 ? 'bg-blue-600/20 border-blue-500/40 text-blue-100' : 'bg-gray-800/80 border-gray-700 text-gray-300'
                              }`}>
                                <div className="flex flex-col items-center text-center justify-start flex-1 mb-2 mt-1">
                                  <span className="text-[14px] sm:text-base font-bold text-white tracking-wide">Guest {guestIndex + 1}</span>
                                  {quantity > 0 ? (
                                    <span className="text-[12px] sm:text-sm text-blue-400 mt-1 font-semibold">{formatPrice(addOn.price * quantity)}</span>
                                  ) : (
                                    <span className="text-[12px] sm:text-sm text-transparent mt-1 font-semibold invisible">{formatPrice(addOn.price)}</span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between w-full bg-[#1a1a1a] rounded-full p-1 border border-white/5">
                                  <button
                                    onClick={() => onQuantityChange(addOnId, guestIndex, Math.max(0, quantity - 1))}
                                    disabled={quantity === 0}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                      quantity === 0 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 hover:bg-white/5 active:bg-white/10'
                                    }`}
                                  >
                                    <svg width="14" height="2" viewBox="0 0 14 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M1 1H13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                                    </svg>
                                  </button>
                                  <span className={`w-8 text-center font-bold text-base ${quantity > 0 ? 'text-white' : 'text-gray-400'}`}>
                                    {quantity}
                                  </span>
                                  <button
                                    onClick={() => onQuantityChange(addOnId, guestIndex, quantity + 1)}
                                    disabled={quantity >= maxForGuest}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                      quantity >= maxForGuest ? 'text-gray-600 cursor-not-allowed' : 'text-white hover:bg-white/5 active:bg-white/10'
                                    }`}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M1 7H13M7 1V13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                           ) : (
                              <GuestAddOnTooltip guestIndex={guestIndex} guestAddOns={guestAddOns} allAddOns={allAddOns}>
                                <button
                                  onClick={() => onToggle(addOnId, !hasAddOn, guestIndex)}
                                  className={`w-full py-1.5 sm:py-3 px-1 sm:px-4 rounded-xl text-[11px] sm:text-sm font-bold transition-all border ${
                                    hasAddOn
                                      ? 'bg-blue-600 text-white border-blue-500'
                                      : 'bg-gray-800/80 text-gray-300 border-gray-700 hover:bg-gray-700'
                                  }`}
                                >
                                  Guest {guestIndex + 1}
                                </button>
                              </GuestAddOnTooltip>
                           )}
                        </div>
                      )
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
       </div>
    </div>
  );
};

export default MobileTableSelectionPopup;
